<template>
  <tab-item-template>
    <template #toolbar-items>
      <el-tooltip placement="bottom" content="サービス構成モデル追加">
        <v-btn
            v-if="isEditable"
            icon
            @click="openCreateDialog">
          <v-icon>mdi-plus</v-icon>
        </v-btn>
      </el-tooltip>
      <el-tooltip placement="bottom" content="選択中の共通辞書からサービス構成モデルを複製" v-if="isCategory2nd('local') && isEditable">
        <v-btn
            icon
            @click="openDuplicateDialog">
          <v-icon>mdi-plus-box-outline</v-icon>
        </v-btn>
      </el-tooltip>
      <v-spacer/>
      <toolbar-inline v-if="isCategory2nd('common')">
        <category-select
            v-model="searchCategory"
            :category-ids="categoryIds"
            clearable
            placeholder="カテゴリ">
        </category-select>
      </toolbar-inline>
      <toolbar-inline>
        <v-text-field
            v-model="searchText"
            clearable
            placeholder="検索">
        </v-text-field>
      </toolbar-inline>
    </template>

    <el-table
        :data="serviceComponentModelsViewFiltered"
        border>
      <!-- 共通辞書の場合 -->
      <template v-if="isCategory2nd('common')">
        <el-table-column
            min-width="30"
            prop="target.name"
            label="名称">
          <template #default="{row}">
            {{ row.target.name }}
            <error-popover :errors="row.errors"></error-popover>
          </template>
        </el-table-column>
        <el-table-column
            prop="target.description"
            label="説明">
          <template #default="{row}">
            <table-inline>
              <text-field-like
                  :value="row.target.description"
                  placeholder="未設定"
                  multi-line>
              </text-field-like>
            </table-inline>
          </template>
        </el-table-column>
      </template>

      <!-- 個別辞書の場合 -->
      <template v-else>
        <el-table-column>
          <template #header>{{ dictionary.name }}</template>
          <el-table-column
              min-width="40"
              prop="local.name"
              label="名称">
            <template #default="{row}">
              {{ row.local.name }}
              <error-popover :errors="row.errors"></error-popover>
            </template>
          </el-table-column>
          <el-table-column
              prop="local.description"
              label="説明">
            <template #default="{row}">
              <table-inline>
                <text-field-like
                    :value="row.local.description"
                    placeholder="未設定"
                    multi-line>
                </text-field-like>
              </table-inline>
            </template>
          </el-table-column>
        </el-table-column>
        <el-table-column>
          <template #header>{{ selectedReferenceDictionary.name }}</template>
          <el-table-column
              min-width="50"
              prop="common.name"
              label="名称">
            <template #default="{row}">
              <table-inline>
                <model-select
                    :value="row.common.id"
                    :items="entities.serviceComponentModel.find({dictionaryId: selectedReferenceDictionary.id})"
                    :groups="entities.category.find({dictionaryId: selectedReferenceDictionary.id})"
                    item-group="categoryIds"
                    :clearable="isEditable"
                    :readonly="!isEditable"
                    @change="setCommonServiceComponentModelId(row.local.id, selectedReferenceDictionary.id, $event)"
                    :expanded-groups="commonSelectExpandedGroups[selectedReferenceDictionary.id]"
                    @change-expanded-groups="$set(commonSelectExpandedGroups, selectedReferenceDictionary.id, $event)"
                    default-expand-all>
                  <template #item="{item, on, attrs}">
                    <v-list-item v-bind="attrs" v-on="on" class="show-label-on-hover dictionary-menu-item">
                      <v-col class="name">
                        <menu-inline>
                          <text-field-like
                              label="名称"
                              :value="item.name"
                              :value-hds-id="item.id">
                          </text-field-like>
                        </menu-inline>
                      </v-col>
                      <v-col>
                        <menu-inline>
                          <text-field-like
                              label="説明"
                              :value="item.description"
                              placeholder="未設定"
                              multi-line>
                          </text-field-like>
                        </menu-inline>
                      </v-col>
                    </v-list-item>
                  </template>
                </model-select>
              </table-inline>
            </template>
          </el-table-column>
          <el-table-column
              prop="common.description"
              label="説明">
            <template #default="{row}">
              <table-inline>
                <text-field-like
                    :value="row.common.description"
                    placeholder="未設定"
                    multi-line>
                </text-field-like>
              </table-inline>
            </template>
          </el-table-column>
        </el-table-column>
      </template>

      <el-table-column
          width="93"
          align="right"
          fixed="right">
        <template #default="{row}">
          <icon-btns>
            <!-- 編集モード -->
            <template v-if="isEditable">
              <icon-btn
                  type="delete"
                  @click="openDeleteDialog(row.target)">
              </icon-btn>
              <icon-btn
                  type="edit"
                  :to="buildNextPageLink(row)"
                  @click="navigateGuardNextPage(row)">
              </icon-btn>
            </template>

            <!-- 閲覧モード -->
            <template v-else>
              <icon-btn
                  type="view"
                  :to="buildNextPageLink(row)"
                  @click="navigateGuardNextPage(row)">
              </icon-btn>
            </template>
          </icon-btns>
        </template>
      </el-table-column>
    </el-table>

    <!-- 作成ダイアログ -->
    <simple-dialog
        v-model="dialogCreate"
        title-text="サービス構成モデル作成"
        @close="closeCreateDialog">
      <v-row>
        <v-col cols="12">
          <v-text-field
              autofocus
              v-model="dialogCreate.name"
              label="サービス構成モデル名"
              hint="後から変更が可能です"
              :rules="[v => !!v || 'サービス構成モデル名が必要です']"/>
        </v-col>
        <v-col cols="12">
          <v-textarea
              auto-grow
              rows="1"
              v-model="dialogCreate.description"
              label="説明"
              messages="複数行入力可">
          </v-textarea>
        </v-col>
      </v-row>
    </simple-dialog>

    <!-- 複製ダイアログ -->
    <simple-dialog
        v-model="dialogDuplicate"
        title-text="サービス構成モデル複製"
        @close="closeDuplicateDialog">
      <v-row>
        <v-col cols="12">
          <model-select
              v-model="dialogDuplicate.serviceComponentModelId"
              :items="entities.serviceComponentModel.find({dictionaryId: selectedReferenceDictionary.id})"
              :groups="entities.category.find({dictionaryId: selectedReferenceDictionary.id})"
              item-group="categoryIds"
              label="選択中の共通辞書に含まれるサービス構成モデル"
              :rules="[v => !!v || 'サービス構成モデルが必要です']"
              default-expand-all
              taller>
            <template #item="{item, on, attrs}">
              <v-list-item v-bind="attrs" v-on="on" class="show-label-on-hover">
                <v-col class="name">
                  <menu-inline>
                    <text-field-like
                        label="名称"
                        :value="item.name"
                        :value-hds-id="item.id">
                    </text-field-like>
                  </menu-inline>
                </v-col>
                <v-col>
                  <menu-inline>
                    <text-field-like
                        label="説明"
                        :value="item.description"
                        placeholder="未設定"
                        multi-line>
                    </text-field-like>
                  </menu-inline>
                </v-col>
              </v-list-item>
            </template>
          </model-select>
        </v-col>
      </v-row>
    </simple-dialog>

    <!-- 削除ダイアログ -->
    <simple-dialog
        v-model="dialogDelete"
        title-text="サービス構成モデル削除"
        title-color="delete"
        apply-text="削除"
        header-text="以下のサービス構成モデルを削除します。本当によろしいですか？"
        @close="closeDeleteDialog">
      <v-row>
        <v-col cols="12">
          <text-field-like
              v-model="dialogDelete.name"
              label="サービス構成モデル名">
          </text-field-like>
        </v-col>
        <v-col cols="12">
          <text-field-like
              v-model="dialogDelete.description"
              label="説明"
              placeholder="未入力"
              multi-line>
          </text-field-like>
        </v-col>
      </v-row>
    </simple-dialog>
  </tab-item-template>
</template>

<script>
import PathInfoMixin from "@/router/PathInfoMixin";
import CategorySelect from "@/components/molecules/CategorySelect.vue";
import SimpleDialog from "@/components/organisms/SimpleDialog.vue";
import {mapGetters, mapMutations, mapActions} from "vuex";
import gqlScmAdd from "@/gql/Dictionary/ServiceComponentModelAdd.gql";
import gqlScmRemove from "@/gql/Dictionary/ServiceComponentModelRemove.gql";
import TabItemTemplate from "@/components/templates/TabItemTemplate.vue";
import {generateTemporaryID} from "@/store/modules/hds";
import TableInline from "@/components/atoms/TableInline.vue";
import ModelSelect from "@/components/molecules/ModelSelect.vue";
import BetterSet from "betterset";
import natsort from "natsort";
import gqlScmDuplicate from "@/gql/Dictionary/ServiceComponentModelDuplicate.gql";
import ErrorPopover from "@/components/molecules/ErrorPopover.vue";
import IconBtns from "@/components/molecules/IconBtns";
import IconBtn from "@/components/molecules/IconBtn.vue";
import TextFieldLike from "@/components/molecules/TextFieldLike.vue";
import ToolbarInline from "@/components/atoms/ToolbarInline.vue";
import ErrorBuilderMixin from "@/components/pages/dictionary/Dictionary/ErrorBuilderMixin";
import DataSourceMixin from "@/components/pages/dictionary/Dictionary/DataSourceMixin";
import MenuInline from "@/components/atoms/MenuInline.vue";

const sortFn = natsort();

export default {
  name: 'ServiceComponentModelList',
  components: {
    MenuInline,
    TextFieldLike,
    IconBtn, IconBtns, ErrorPopover, ModelSelect, TableInline, TabItemTemplate, SimpleDialog, CategorySelect,
    ToolbarInline
  },
  mixins: [
    PathInfoMixin,
    DataSourceMixin,
    ErrorBuilderMixin,
  ],
  props: {
    dictionary: {
      type: Object,
      required: false
    },
    selectedReferenceDictionary: {
      type: Object,
      required: false,
      default() {
        return {};
      }
    }
  },
  data() {
    return {
      searchCategory: null,
      searchText: null,
      dialogCreate: false,
      dialogDuplicate: false,
      dialogDelete: false,
      serviceComponentModelsView: [],
      commonSelectExpandedGroups: {},
    };
  },
  computed: {
    ...mapGetters({
      entities: 'hds/entities',
    }),
    serviceComponentModelsViewFiltered() {
      if (this.searchCategory || this.searchText) {
        return this.serviceComponentModelsView.filter(scmView => {
          const t = scmView.target;
          return (!this.searchCategory ||
                  t.categoryIds.includes(this.searchCategory)) &&
              (!this.searchText ||
                  t.name.includes(this.searchText) ||
                  t.description.includes(this.searchText));
        });
      } else {
        return this.serviceComponentModelsView;
      }
    },
    categoryIds() {
      return this.serviceComponentModels
          .flatMap(dc => dc.categoryIds)
          .reduce((set, categoryId) => { set.add(categoryId); return set; }, new BetterSet())
          .asArray();
    },
    selectedReferenceDictionaryId() {
      return this.selectedReferenceDictionary.id;
    },
  },
  methods: {
    ...mapMutations({
      showSnackbar: 'showSnackbar'
    }),
    ...mapActions({
      gqlMutate: 'hds/gqlMutate',
      gqlMutateAndLoad: 'hds/gqlMutateAndLoad'
    }),
    getCommonServiceComponentModelId(sourceServiceComponentModelId, targetDictionaryId) {
      const dtm = this.entities.serviceTranslationMap.findOne({
        sourceServiceComponentModelId,
        targetDictionaryId
      });
      return dtm ? dtm.targetServiceComponentModelId : null;
    },
    setCommonServiceComponentModelId(sourceServiceComponentModelId, targetDictionaryId, targetServiceComponentModelId) {
      this.entities.serviceTranslationMap.findAndRemove({
        sourceServiceComponentModelId,
        targetDictionaryId
      });
      if(targetServiceComponentModelId) {
        this.entities.serviceTranslationMap.insert({
          id: generateTemporaryID(),
          sourceServiceComponentModelId,
          targetServiceComponentModelId,
          targetDictionaryId
        });
      }
      this.updateServiceComponentModelsView();
      this.$emit('dirty');
    },
    updateServiceComponentModelsView() {
      if (this.isCategory2nd('common')) {
        this.serviceComponentModelsView = this.serviceComponentModels.map(commonScm => ({
          target: commonScm,
          common: commonScm,
          local: null,
          errors: this.buildServiceComponentModelError(commonScm),
        }));
      } else {
        this.serviceComponentModelsView = this.serviceComponentModels.map(localScm => {
          const commonScmId = this.getCommonServiceComponentModelId(localScm.id, this.selectedReferenceDictionary.id);
          const commonScm = commonScmId ? this.entities.serviceComponentModel.findOne({id: commonScmId}) : null;
          return {
            target: localScm,
            common: commonScm || {},
            local: localScm,
            errors: this.buildServiceComponentModelError(localScm),
          }
        });
      }

      // ソート
      this.serviceComponentModelsView
          .sort((a, b) => sortFn(a.name, b.name));
    },
    buildNextPageLink(row) {
      if(!row.common.id) {
        return null;
      }
      return {
        name: this.alterRouteName('ServiceComponentModel'),
        params: {
          dictionaryId: this.dictionary.id,
          serviceComponentModelId: row.target.id
        },
        query: {
          selectedDictionary: this.isCategory2nd('common') ? undefined : this.selectedReferenceDictionary.id
        }
      };
    },
    navigateGuardNextPage(row) {
      this.showSnackbar({
        visible: true,
        color: 'warn',
        message: '共通辞書のサービス構成モデルが選択されていない為、遷移できません'
      });
    },
    openDuplicateDialog() {
      this.dialogDuplicate = {
        serviceComponentModelId: null,
        dictionaryId: this.dictionary.id
      };
    },
    closeDuplicateDialog(apply) {
      if (apply) {
        const newEntityName = this.entities.serviceComponentModel.findOne({id: this.dialogDuplicate.serviceComponentModelId}).name;
        this.showSnackbar({
          message: `サービス構成モデル「${newEntityName}」を複製中です`,
          timeout: false,
          loading: true
        });
        this.gqlMutateAndLoad({
              mutation: gqlScmDuplicate,
              variables: {
                sourceServiceComponentModelId: this.dialogDuplicate.serviceComponentModelId,
                destinationDictionaryId: this.dictionary.id
              },
              mapping: {
                '-': 'duplicateServiceComponentModel.dictionary-',
                serviceComponentModel: 'duplicateServiceComponentModel',
                serviceTranslationMap: 'duplicateServiceComponentModel.serviceTranslationMaps-',
                dataComponentModel: 'duplicateServiceComponentModel.dictionary.dataComponentModels*-',
                dataTranslationMap: 'duplicateServiceComponentModel.dictionary.dataComponentModels.dataTranslationMaps*-',
              }
            })
            .then(data => {
              this.showSnackbar({
                message: `サービス構成モデル「${newEntityName}」を複製しました`
              });
              this.updateServiceComponentModelsView();
            }, () => {
              this.showSnackbar({
                color: 'failed',
                message: `サービス構成モデル「${newEntityName}」の複製に失敗しました`
              });
            });
      }
      this.dialogDuplicate = false;
    },
    openCreateDialog() {
      this.dialogCreate = {
        dictionaryId: this.dictionary.id,
        name: '',
        description: '',
        categoryIds: []
      };
    },
    closeCreateDialog(apply) {
      if (apply) {
        const newEntity = this.dialogCreate;
        this.showSnackbar({
          message: `サービス構成モデル「${newEntity.name}」を作成中です`,
          timeout: false,
          loading: true
        });
        this.gqlMutate({
              mutation: gqlScmAdd,
              variables: {
                addServiceComponentModel: newEntity
              }
            })
            .then(data => {
              data.addServiceComponentModels.forEach(e => {
                this.entities.serviceComponentModel.insert(e);
              });
              this.showSnackbar({
                message: `サービス構成モデル「${newEntity.name}」を作成しました`
              });
            }, () => {
              this.showSnackbar({
                color: 'failed',
                message: `サービス構成モデル「${newEntity.name}」の作成に失敗しました`
              });
            });
      }
      this.dialogCreate = false;
    },
    openDeleteDialog(row) {
      this.dialogDelete = row;
    },
    closeDeleteDialog(apply) {
      if (apply) {
        const removeEntity = this.dialogDelete;
        this.showSnackbar({
          message: `サービス構成モデル「${removeEntity.name}」を削除中です`,
          timeout: false,
          loading: true
        });
        this.gqlMutate({
              mutation: gqlScmRemove,
              variables: {
                removeServiceComponentModels: [removeEntity.id]
              }
            })
            .then(data => {
              this.entities.serviceComponentModel.remove(removeEntity);
              this.showSnackbar({
                message: `サービス構成モデル「${removeEntity.name}」が削除されました`
              });
            }, () => {
              this.showSnackbar({
                color: 'failed',
                message: `サービス構成モデル「${removeEntity.name}」の削除に失敗しました`
              });
            });
      }
      this.dialogDelete = false;
    }
  },
  watch: {
    serviceComponentModels: {
      immediate: true,
      handler() {
        this.updateServiceComponentModelsView();
      }
    },
    selectedReferenceDictionary() {
      this.updateServiceComponentModelsView();
    }
  }
}
</script>

<style lang="scss" scoped>
.dictionary-menu-item {
  max-width: 800px;
  min-width: 500px;
}

.col.name {
  max-width: 200px;
}
</style>
